import React, { useState } from "react";

interface LiveCameraPermissionModalProps {
  isOpen: boolean;
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
  logStep?: (eventName: string, payload?: any) => void;
}

const LiveCameraPermissionModal: React.FC<LiveCameraPermissionModalProps> = ({
  isOpen,
  onPermissionGranted,
  onPermissionDenied,
  logStep,
}) => {
  const [isRequesting, setIsRequesting] = useState(false);

  const handleOkClick = async () => {
    setIsRequesting(true);
    try {
      // Request camera permission from browser
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      // Permission granted! Stop the stream and save to localStorage
      stream.getTracks().forEach((track) => track.stop());
      localStorage.setItem("cameraPermissionGranted", "true");

      // Log camera permission granted (non-blocking)
      if (logStep) {
        logStep("camera_permission_granted");
      }

      // Call the onPermissionGranted callback
      onPermissionGranted();
    } catch (error) {
      console.error("Camera permission denied:", error);

      // Log camera permission denied (non-blocking)
      if (logStep) {
        logStep("camera_permission_denied", {
          error_message: "User denied camera permission",
        });
      }

      // Permission denied - call onPermissionDenied
      onPermissionDenied();
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDontAllow = () => {
    // Log camera permission denied (non-blocking)
    if (logStep) {
      logStep("camera_permission_denied", {
        error_message: "User chose not to allow camera permission",
      });
    }

    // Call the onPermissionDenied callback
    onPermissionDenied();
  };

  if (!isOpen) return null;

  return (
    <div
      className="d-md-none"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: "Sweet Sans Pro Regular",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "20px",
          textAlign: "left",
          maxWidth: "300px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "400",
            marginBottom: "8px",
            color: "#000",
            fontFamily: "Sweet Sans Pro Regular",
          }}
        >
          &ldquo;Senco Gold&rdquo; Would Like To Access Your Camera
        </h3>
        <p
          style={{
            fontSize: "13px",
            color: "#666",
            marginBottom: "20px",
            lineHeight: "1.4",
          }}
        >
          This app requires access to camera
        </p>
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <button
            onClick={handleDontAllow}
            disabled={isRequesting}
            style={{
              flex: 1,
              padding: "9px 10px",
              backgroundColor: "#f0f0f0",
              border: "none",
              borderRadius: "20px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#333",
              cursor: isRequesting ? "not-allowed" : "pointer",
              opacity: isRequesting ? 0.6 : 1,
              fontFamily: "Sweet Sans Pro Regular",
            }}
          >
            Don&apos;t Allow
          </button>
          <button
            onClick={handleOkClick}
            disabled={isRequesting}
            style={{
              flex: 1,
              padding: "9px 10px",
              backgroundColor: "#007AFF",
              border: "none",
              borderRadius: "20px",
              fontSize: "15px",
              fontWeight: "500",
              color: "white",
              cursor: isRequesting ? "not-allowed" : "pointer",
              opacity: isRequesting ? 0.6 : 1,
              fontFamily: "Sweet Sans Pro Regular",
            }}
          >
            {isRequesting ? "Requesting..." : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveCameraPermissionModal;
